import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip, Trash2, FileImage, ImageOff, FileText, Maximize2, Loader2 } from 'lucide-react';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB raw input
const ACCEPT = "image/*,application/pdf";

// Compression target for image receipts. Keeps quality high enough for OCR
// while reducing typical phone photos by 60–85%.
const COMPRESS_MAX_DIMENSION = 1600;
const COMPRESS_QUALITY = 0.8;

/**
 * Reads a File and returns a Data URL of a JPEG-compressed, scaled-down
 * version of the image. Falls back to the original file if compression
 * is not beneficial (e.g. for tiny images already smaller than the target).
 */
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Archivo de imagen no válido'));
      img.onload = () => {
        const ratio = img.width > img.height
          ? COMPRESS_MAX_DIMENSION / img.width
          : COMPRESS_MAX_DIMENSION / img.height;
        const scale = ratio < 1 ? ratio : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve({ dataUrl: reader.result, originalSize: file.size, finalSize: file.size });
              return;
            }
            // If compression made it BIGGER (rare, small images), keep original
            if (blob.size >= file.size) {
              resolve({ dataUrl: reader.result, originalSize: file.size, finalSize: file.size });
              return;
            }
            const fr = new FileReader();
            fr.onerror = () => reject(new Error('No se pudo procesar la imagen'));
            fr.onload = () => resolve({ dataUrl: String(fr.result), originalSize: file.size, finalSize: blob.size });
            fr.readAsDataURL(blob);
          },
          'image/jpeg',
          COMPRESS_QUALITY
        );
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    r.onload = () => resolve(String(r.result || ''));
    r.readAsDataURL(file);
  });
}

function humanSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Reusable receipt attachment uploader.
 * Stores files as a data URL string (data:<mime>;base64,<...>).
 * Images are auto-compressed (max 1600px, JPEG q=0.8) before encoding.
 */
export default function ReceiptUploader({ value, onChange, testId = "receipt" }) {
  const fileRef = useRef(null);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);

  const handleFile = async (file) => {
    setError("");
    setCompressionInfo(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError("El archivo supera 5 MB. Comprime la imagen o usa un PDF más liviano.");
      return;
    }
    setProcessing(true);
    try {
      if (file.type.startsWith('image/')) {
        const { dataUrl, originalSize, finalSize } = await compressImage(file);
        const savedPct = originalSize > 0
          ? Math.max(0, Math.round((1 - finalSize / originalSize) * 100))
          : 0;
        setCompressionInfo({ originalSize, finalSize, savedPct });
        onChange(dataUrl);
      } else {
        const url = await readAsDataURL(file);
        setCompressionInfo({ originalSize: file.size, finalSize: file.size, savedPct: 0 });
        onChange(url);
      }
    } catch (e) {
      setError(e.message || 'Error procesando el archivo');
    } finally {
      setProcessing(false);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = () => {
    setError("");
    setCompressionInfo(null);
    onChange("");
  };

  const hasReceipt = !!value;
  const isImage = hasReceipt && value.startsWith("data:image/");
  const isPdf   = hasReceipt && value.startsWith("data:application/pdf");

  return (
    <div className="space-y-2" data-testid={`${testId}-uploader`}>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        capture="environment"
        onChange={onInputChange}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />

      {!hasReceipt && !processing && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-violet-500 hover:bg-zinc-900/40 transition-colors rounded-lg py-3 text-zinc-400 text-sm"
          data-testid={`${testId}-upload-btn`}
        >
          <Paperclip className="h-4 w-4" /> Adjuntar recibo (imagen o PDF, ≤ 5 MB)
        </button>
      )}

      {processing && (
        <div className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-700 rounded-lg py-3 text-zinc-400 text-sm" data-testid={`${testId}-processing`}>
          <Loader2 className="h-4 w-4 animate-spin" /> Procesando imagen…
        </div>
      )}

      {hasReceipt && !processing && (
        <div className="flex items-center gap-3 p-2 rounded-lg border border-zinc-700 bg-zinc-900/40" data-testid={`${testId}-preview`}>
          {isImage ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="relative h-14 w-14 rounded overflow-hidden border border-zinc-700 group shrink-0"
              aria-label="Ver recibo grande"
              data-testid={`${testId}-thumbnail`}
            >
              <img src={value} alt="recibo" className="h-full w-full object-cover" />
              <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <Maximize2 className="h-4 w-4 text-white" />
              </span>
            </button>
          ) : isPdf ? (
            <div className="h-14 w-14 rounded flex items-center justify-center bg-zinc-800 shrink-0">
              <FileText className="h-6 w-6 text-violet-400" />
            </div>
          ) : (
            <div className="h-14 w-14 rounded flex items-center justify-center bg-zinc-800 shrink-0">
              <FileImage className="h-6 w-6 text-zinc-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-200 truncate">
              {isImage ? "Imagen adjunta" : isPdf ? "PDF adjunto" : "Archivo adjunto"}
            </p>
            <p className="text-xs text-zinc-500">
              ~{Math.round((value.length * 3) / 4 / 1024)} KB
              {compressionInfo && compressionInfo.savedPct > 0 && (
                <span className="ml-2 text-emerald-400" data-testid={`${testId}-savings`}>
                  −{compressionInfo.savedPct}% ({humanSize(compressionInfo.originalSize)} → {humanSize(compressionInfo.finalSize)})
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-zinc-300 hover:text-violet-300 h-8"
              onClick={() => fileRef.current?.click()}
              data-testid={`${testId}-replace-btn`}
            >
              Reemplazar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-zinc-400 hover:text-red-400"
              onClick={remove}
              aria-label="Quitar recibo"
              data-testid={`${testId}-remove-btn`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1" data-testid={`${testId}-error`}>
          <ImageOff className="h-3 w-3" /> {error}
        </p>
      )}

      {previewOpen && isImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
          aria-label="Vista previa del recibo"
        >
          <img
            src={value}
            alt="recibo grande"
            className="max-h-[90vh] max-w-[90vw] rounded shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
